//components/admin/pagebuilder

import React, { useState, useEffect, useCallback } from "react";
import { DragDropContext } from "@hello-pangea/dnd";
import { v4 as uuid } from "uuid";

import BuilderSidebar from "./pagebuilder/BuilderSidebar";
import BuilderCanvas from "./pagebuilder/BuilderCanvas";
import EditModal from "./pagebuilder/EditModal";
import SeoModal from "./pagebuilder/pageModal/SeoModal";
import VersionsModal from "./pagebuilder/pageModal/VersionsModal";

import { loadPage, savePage } from "../../api/pageApi";

import {
  homeTemplate,
  landingTemplate,
  courseTemplate,
  forumTemplate,
} from "./pagebuilder/templates/pageTemplates";

export default function PageBuilder({ slug = "home" }) {
  const [sections, setSections] = useState([]);
  const [seo, setSeo] = useState({});
  const [editing, setEditing] = useState(null);

  const [showSeo, setShowSeo] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [loading, setLoading] = useState(true);

  const [preview, setPreview] = useState("desktop");

  // ===============================
  // LOAD PAGE
  // ===============================
  const loadCurrentPage = async () => {
    const res = await loadPage(slug);
    if (res.data?.exists) {
      setSections(res.data.page.content?.sections || []);
      setSeo(res.data.page.content?.seo || {});
    }
  };

  useEffect(() => {
    loadCurrentPage().finally(() => setLoading(false));
  }, [slug]);

  // ===============================
  // SAVE PAGE
  // ===============================
  const handleSave = async () => {
    await savePage(slug, {
      title: seo.title || slug,
      content: { seo, sections },
    });
    alert("Página salva com sucesso");
  };

  // ===============================
  // DRAG END (CORRIGIDO)
  // ===============================
  const onDragEnd = useCallback((result) => {
    const { source, destination } = result;
    if (!destination) return;

    // Sidebar → coluna
    if (source.droppableId === "sidebar") {
      const componentType = source.draggableId;
      if (!componentType) return;

      const [, sectionId, colKey] = destination.droppableId.split(":");

      setSections((prev) =>
        prev.map((s) =>
          s.id === sectionId
            ? {
                ...s,
                columns: {
                  ...s.columns,
                  [colKey]: [
                    ...s.columns[colKey],
                    {
                      id: uuid(),
                      type: componentType,
                      data: {},
                    },
                  ],
                },
              }
            : s
        )
      );
      return;
    }

    // Reordenar dentro da mesma coluna
    if (source.droppableId === destination.droppableId) {
      const [, sectionId, colKey] = source.droppableId.split(":");

      setSections((prev) =>
        prev.map((s) => {
          if (s.id !== sectionId) return s;
          const items = Array.from(s.columns[colKey]);
          const [moved] = items.splice(source.index, 1);
          items.splice(destination.index, 0, moved);

          return {
            ...s,
            columns: { ...s.columns, [colKey]: items },
          };
        })
      );
    }
  }, []);

  if (loading) return <div className="p-10">Carregando…</div>;

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      {/* TOPO */}
      <div className="flex items-center justify-between gap-4 p-4 border-b bg-white">
        <div className="flex gap-2">
          <button onClick={() => setSections(homeTemplate())} className="btn">Home</button>
          <button onClick={() => setSections(landingTemplate())} className="btn">Landing</button>
          <button onClick={() => setSections(courseTemplate())} className="btn">Curso</button>
          <button onClick={() => setSections(forumTemplate())} className="btn">Fórum</button>
        </div>

        <div className="flex gap-2">
          {["desktop","tablet","mobile"].map(p => (
            <button
              key={p}
              onClick={() => setPreview(p)}
              className={`btn ${preview===p?"btn-primary":""}`}
            >
              {p}
            </button>
          ))}
          <button onClick={() => setShowSeo(true)} className="btn">SEO</button>
          <button onClick={() => setShowVersions(true)} className="btn">Versões</button>
        </div>
      </div>

      {/* EDITOR */}
      <div className="flex h-screen bg-gray-100">
        <BuilderSidebar />
        <BuilderCanvas
          sections={sections}
          preview={preview}
          onAddSection={(layout) =>
            setSections((s) => [
              ...s,
              {
                id: uuid(),
                layout,
                columns:
                  layout === "single"
                    ? { col1: [] }
                    : layout === "two"
                    ? { col1: [], col2: [] }
                    : layout === "three"
                    ? { col1: [], col2: [], col3: [] }
                    : { col1: [], col2: [], col3: [], col4: [] },
              },
            ])
          }
          onEdit={setEditing}
        />
      </div>

      <button
        onClick={handleSave}
        className="fixed bottom-6 right-6 bg-indigo-600 text-white px-6 py-3 rounded-xl shadow"
      >
        Salvar Página
      </button>

      {editing && (
        <EditModal
          component={editing}
          onClose={() => setEditing(null)}
          onSave={(data) => {
            setSections((prev) =>
              prev.map((s) => ({
                ...s,
                columns: Object.fromEntries(
                  Object.entries(s.columns).map(([k, col]) => [
                    k,
                    col.map((c) =>
                      c.id === editing.id ? { ...c, data } : c
                    ),
                  ])
                ),
              }))
            );
            setEditing(null);
          }}
        />
      )}

      {showSeo && (
        <SeoModal
          seo={seo}
          onClose={() => setShowSeo(false)}
          onSave={(data) => {
            setSeo(data);
            setShowSeo(false);
          }}
        />
      )}

      {showVersions && (
        <VersionsModal
          slug={slug}
          onClose={() => setShowVersions(false)}
          onRestored={async () => {
            await loadCurrentPage();
            setShowVersions(false);
          }}
        />
      )}
    </DragDropContext>
  );
}
